import React from 'react'
import { PageLayout } from '../components/common/PageLayout'
import { Section } from '../components/common/DesignComponents'

export function PrivacyPage(): React.ReactElement {
  const lastUpdated = 'January 2024'

  const sections = [
    {
      title: 'Introduction',
      content: 'BabyKingdom is committed to protecting the privacy of children and their families. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.'
    },
    {
      title: 'Information We Collect',
      subsections: [
        {
          heading: 'From Parents/Guardians:',
          items: ['Email address and password', 'Name and contact information', 'Payment information (for subscriptions)', 'Device information and usage data']
        },
        {
          heading: 'From Children:',
          items: ['Profile name and age', 'Game progress and achievements', 'Coins and inventory data', 'Activity logs (not personal information)']
        }
      ]
    },
    {
      title: 'How We Use Your Information',
      items: [
        'To provide and improve our services',
        'To send newsletters and updates (with parent consent)',
        'To personalize your child\'s learning experience',
        'To maintain account security',
        'To comply with legal obligations'
      ]
    },
    {
      title: 'Children\'s Privacy Protection',
      content: 'BabyKingdom complies with the Children\'s Online Privacy Protection Act (COPPA). We do not knowingly collect personal information from children under 13 without parental consent. Parents have the right to review, delete, or refuse further collection of their child\'s information.'
    },
    {
      title: 'Data Security',
      content: 'We implement industry-standard encryption and security measures to protect your information. All data is transmitted over secure HTTPS connections, and we regularly audit our security practices. However, no method of transmission over the internet is 100% secure.'
    },
    {
      title: 'Information Sharing',
      content: 'We do NOT sell or share your personal information with third parties for marketing purposes. We may share information with service providers who assist us in operating our website and conducting our business, under strict confidentiality agreements. We may also disclose information if required by law.'
    },
    {
      title: 'Cookies and Tracking',
      content: 'We use cookies and similar tracking technologies to enhance your experience. Parents can control cookie settings through their browser. We do not use third-party tracking for behavioral advertising directed at children.'
    },
    {
      title: 'Parental Rights',
      items: [
        'Request access to your child\'s personal information',
        'Review, update, or delete your child\'s data',
        'Opt out of newsletters and promotional emails',
        'Disable your child\'s account',
        'Request deletion of all collected data'
      ]
    },
    {
      title: 'Third-Party Links',
      content: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any information.'
    },
    {
      title: 'Policy Updates',
      content: 'We may update this Privacy Policy periodically. We will notify you of significant changes by email or through the website. Your continued use of BabyKingdom after updates constitutes your acceptance of the new policy.'
    },
    {
      title: 'Contact Us',
      content: 'If you have questions about this Privacy Policy or our privacy practices, please contact us at privacy@babykingdom.com or by mail at: BabyKingdom Privacy Team, 123 Learning Lane, Education City, EC 12345'
    }
  ]

  return (
    <PageLayout title="Privacy Policy" subtitle="How we protect your family's information">
      <Section title="">
        <div className="mb-8 p-6 bg-blue-50 border-4 border-blue-200 rounded-2xl">
          <p className="text-gray-700 font-bold">
            <strong>Last Updated:</strong> {lastUpdated}
          </p>
          <p className="text-gray-700 font-bold mt-2">
            🔒 Your family's privacy is our top priority. We take data protection seriously and comply with all applicable laws.
          </p>
        </div>

        <div className="space-y-8">
          {sections.map((section, idx) => (
            <div key={idx} className="border-l-8 border-purple-400 pl-6">
              <h3 className="text-2xl font-black text-purple-700 mb-4">{section.title}</h3>

              {section.content && (
                <p className="text-gray-700 font-semibold mb-4">{section.content}</p>
              )}

              {section.subsections && (
                <div className="space-y-4">
                  {section.subsections.map((subsection, sIdx) => (
                    <div key={sIdx}>
                      <h5 className="font-black text-purple-600 mb-2">{subsection.heading}</h5>
                      <ul className="list-disc list-inside space-y-2">
                        {subsection.items.map((item, iIdx) => (
                          <li key={iIdx} className="text-gray-700 font-semibold">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {section.items && !section.subsections && (
                <ul className="list-disc list-inside space-y-2">
                  {section.items.map((item, iIdx) => (
                    <li key={iIdx} className="text-gray-700 font-semibold">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 p-8 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl border-4 border-purple-300">
          <h4 className="text-2xl font-black text-purple-700 mb-4">📋 Your Privacy Rights at a Glance</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <p className="text-gray-700 font-bold">We do NOT sell your data</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <p className="text-gray-700 font-bold">We comply with COPPA regulations</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <p className="text-gray-700 font-bold">We use industry-standard encryption</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <p className="text-gray-700 font-bold">You can delete data anytime</p>
            </div>
          </div>
        </div>
      </Section>
    </PageLayout>
  )
}
